import { createSelector } from 'reselect';

import * as utils from './../../util';
import * as apiAdapter from './../../api-adapter';
import * as constants from './../../constants';
import * as urlQueryString from './../../urlQueryString';
import * as rootCauseUtils from './utils';

const routerLocation = (store) => store.router.location;
const filtersStore = (store) => store.apps.analytics.filters;
const rootCausesStore = (store) => store.apps.analytics.rootCauses;
const failureAnalysisStore = (store) => store.apps.analytics.failureAnalysis;
const userStore = (store) => store.user;

/**
 * Get ownerType and username either from queryString or from selected user or owner filter
 * queryString is first priority, get ownerValue if isQueryStringFilter option is set to true
 * user filter (only for admin) is second priority, return selectedUser with ownerType: user
 * or get the ownerValue from selected owner filter
 */
export const ownerFilterSelector = createSelector([
  userStore,
  failureAnalysisStore,
  filtersStore,
  routerLocation,
  (store, options) => options,
],
(user, failureAnalysis, filters, location, options = {}) => {
  const ownerQueryString = options.isQueryStringFilter ?
    rootCauseUtils.getOwnerFromQueryStringForRootCause(location, user) :
    null;

  if (ownerQueryString) {
    return ownerQueryString;
  }

  const selectedUser = failureAnalysis.getIn(['jobOwnersFilter', 'selectedItems']).first();
  if (selectedUser) {
    return {
      ownerType: 'user',
      username: selectedUser,
    };
  }

  const selectedOwnerFilter = filters.getIn(['filters', 'owners', 'selectedItems']).first();
  return rootCauseUtils.getSelectedOwnerForRootCause(selectedOwnerFilter, user);
});

export const rootCausesSelector = createSelector(
  failureAnalysisStore,
  rootCausesStore,
  routerLocation,
  (failureAnalysis, rootCauses, location) => ({
    loadingData: rootCauses.get('loadingData'),
    items: rootCauses.get('items'),
    selectedTest: rootCauses.get('selectedTest'),
    getRootCauseError: rootCauses.get('getRootCauseError'),
    patternIdQueryString: urlQueryString.get(
      location, constants.ROOTCAUSE_QUERY_STRINGS.PATTERN_ID,
    ),
  }));

export const initFilterSelector = createSelector([
  routerLocation,
], (location) => {
  const selectedTest = urlQueryString.get(location, constants.ROOTCAUSE_QUERY_STRINGS.TEST_HASH);
  return selectedTest ? utils.parseJSON(selectedTest) : null;
});

export const rootCausesParamsSelector = createSelector([
  filtersStore,
  rootCausesStore,
  failureAnalysisStore,
  userStore,
  routerLocation,
  ownerFilterSelector,
  (store, options) => options,
], (filters, rootCauses, failureAnalysis, user, location, selectedOwner, options) => {
  const { isQueryStringFilter, dateBase } = options;
  const { dateFilter, testHash } = urlQueryString.getAll(location);
  const selectedTest = rootCauses.get('selectedTest') !== null ? rootCauses.getIn(['selectedTest', 'id']) : null;
  const defaultParams = {
    ...selectedOwner,
    ...(selectedTest ? { test_hash: selectedTest } : {}),
    ...apiAdapter.getTimeFilterParams(filters.get('selectedDateFilter'), dateBase),
  };

  if (isQueryStringFilter) {
    const parsedTestHash = utils.parseJSON(testHash);

    return {
      ...defaultParams,
      ...(parsedTestHash?.id ? { test_hash: parsedTestHash.id } : {}),
      ...(dateFilter && apiAdapter.getTimeFilterParams(
        utils.getDateFilterFromQueryParams({ dateFilter }), dateBase,
      )),
    };
  }

  return defaultParams;
});

export const selectTestParamsSelector = createSelector([
  routerLocation,
  failureAnalysisStore,
  filtersStore,
  (store, options) => options,
], (location, failureAnalysis, filters, options) => {
  const selectedUser = failureAnalysis.getIn(['jobOwnersFilter', 'selectedItems']).first();
  const selectedOwnerFilter = filters.getIn(['filters', 'owners', 'selectedItems']).first();

  return !options ?
    urlQueryString.remove(location, [
      constants.ROOTCAUSE_QUERY_STRINGS.TEST_HASH,
      constants.ROOTCAUSE_QUERY_STRINGS.PATTERN_ID,
    ]) :
    urlQueryString.set(location, {
      [constants.ROOTCAUSE_QUERY_STRINGS.TEST_HASH]: JSON.stringify(options.test),
      [constants.ROOTCAUSE_QUERY_STRINGS.PATTERN_ID]: options.pattern,
      ...(selectedUser ?
        { [constants.ROOTCAUSE_QUERY_STRINGS.USER]: selectedUser } :
        {}),
      ...(selectedOwnerFilter?.name ?
        { [constants.ROOTCAUSE_QUERY_STRINGS.OWNERS]: selectedOwnerFilter.name } :
        {}),
    });
});

export const rootCauseDataSelector = createSelector([
  rootCausesStore,
  (store, options) => options,
], (rootCauses, options) => rootCauses.getIn(['items', options.patternId]));

export const rootCauseAdditionalDataSelector = createSelector([
  rootCausesStore,
  (store, options) => options,
], (rootCauses, options) => {
  const rootCauseItem = rootCauses.getIn(['items', options.patternId]);
  const annotations = rootCauseItem.getIn(['annotations', 'data']);
  const jobs = rootCauseItem.getIn(['jobs', 'data']);
  return {
    isDataExists: !!(annotations?.length && jobs?.length),
  };
});
